import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import {
  PRIORITY_FILTER_OPTIONS,
  SORT_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from "../../lib/constants";
import type { IncidentFilters } from "../../lib/filterUtils";
import CustomerSearchAutocomplete from "../../../../components/customers/CustomerSearchAutocomplete";
import FilterPanel from "../../../../components/FilterPanel";
import FilterSearchField from "../../../../components/FilterPanel/FilterSearchField";
import IconAutocomplete, { withIcon } from "../../../../components/forms/IconAutocomplete";
import type { Customer } from "../../../../services/customerService";

type IncidentAdvancedFiltersProps = {
  filters: IncidentFilters;
  onChange: (patch: Partial<IncidentFilters>) => void;
  onClear: () => void;
  uniqueAssignees: string[];
  uniqueGroups: string[];
  uniqueCategories: string[];
  activeFiltersCount: number;
  selectedCustomer: Customer | null;
  onCustomerChange: (customer: Customer | null) => void;
};

function FilterCell({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        width: "100%",
        "& .MuiFormControl-root, & .MuiAutocomplete-root": {
          width: "100%",
        },
      }}
    >
      {children}
    </Box>
  );
}

export default function IncidentAdvancedFilters({
  filters,
  onChange,
  onClear,
  uniqueAssignees,
  uniqueGroups,
  uniqueCategories,
  activeFiltersCount,
  selectedCustomer,
  onCustomerChange,
}: IncidentAdvancedFiltersProps) {
  const { t } = useTranslation();
  const searchKind: "text" | "openSearchId" = filters.openSearchId.trim() ? "openSearchId" : "text";
  const [kind, setKind] = useState<"text" | "openSearchId">(searchKind);

  useEffect(() => {
    setKind(searchKind);
  }, [searchKind]);

  const searchKindOptions = [
    { value: "text" as const, label: t("incidents.searchTypeText"), icon: "title" },
    { value: "openSearchId" as const, label: t("incidents.searchTypeDatabaseId"), icon: "fingerprint" },
  ];

  const assigneeOptions = withIcon(
    [{ value: "all", label: t("incidents.allAssignees") }, ...uniqueAssignees.map((a) => ({ value: a, label: a }))],
    (option) => (option.value === "all" ? "filter_list" : "person"),
  );

  const groupOptions = withIcon(
    [{ value: "all", label: t("incidents.allGroups") }, ...uniqueGroups.map((g) => ({ value: g, label: g }))],
    (option) => (option.value === "all" ? "filter_list" : "groups"),
  );

  const categoryOptions = withIcon(
    [
      { value: "all", label: t("incidents.allCategories") },
      ...uniqueCategories.map((c) => ({ value: c, label: c })),
    ],
    (option) => (option.value === "all" ? "filter_list" : "category"),
  );

  const sortOrderOptions = [
    { value: "desc" as const, label: t("incidents.newestFirst"), icon: "south" },
    { value: "asc" as const, label: t("incidents.oldestFirst"), icon: "north" },
  ];

  const handleKindChange = (next: "text" | "openSearchId" | null) => {
    if (!next) return;
    setKind(next);
    onChange({ searchTerm: "", openSearchId: "" });
  };

  return (
    <FilterPanel
      flush
      activeCount={activeFiltersCount}
      onClear={onClear}
      bodySx={{
        display: "block",
        px: 1.5,
        pt: 1.5,
        pb: 1.5,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
          alignItems: "start",
        }}
      >
        <FilterCell>
          <IconAutocomplete
            options={searchKindOptions}
            value={kind}
            onChange={handleKindChange}
            label={t("incidents.searchType")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <FilterSearchField
            value={kind === "text" ? filters.searchTerm : filters.openSearchId}
            onApply={(next) =>
              kind === "text"
                ? onChange({ searchTerm: next, openSearchId: "" })
                : onChange({ openSearchId: next, searchTerm: "" })
            }
            placeholder={
              kind === "text" ? t("incidents.searchIncidents") : t("incidents.searchByOpenSearchId")
            }
          />
        </FilterCell>
        <FilterCell>
          <CustomerSearchAutocomplete
            value={selectedCustomer}
            onChange={onCustomerChange}
            label={t("incidents.customer")}
            placeholder={t("incidents.searchCustomerByName")}
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={PRIORITY_FILTER_OPTIONS}
            value={filters.selectedPriority}
            onChange={(value) => onChange({ selectedPriority: value || "all" })}
            label={t("incidents.priority")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={STATUS_FILTER_OPTIONS}
            value={filters.selectedStatus}
            onChange={(value) => onChange({ selectedStatus: value || "all" })}
            label={t("incidents.status")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={assigneeOptions}
            value={filters.selectedAssignee}
            onChange={(value) => onChange({ selectedAssignee: value || "all" })}
            label={t("incidents.assignee")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={groupOptions}
            value={filters.selectedGroup}
            onChange={(value) => onChange({ selectedGroup: value || "all" })}
            label={t("incidents.group")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={categoryOptions}
            value={filters.selectedCategory}
            onChange={(value) => onChange({ selectedCategory: value || "all" })}
            label={t("incidents.category")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <TextField
            size="small"
            fullWidth
            label={t("incidents.fromDate")}
            type="date"
            value={filters.dateRange.start || ""}
            onChange={(e) =>
              onChange({ dateRange: { ...filters.dateRange, start: e.target.value || null } })
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </FilterCell>
        <FilterCell>
          <TextField
            size="small"
            fullWidth
            label={t("incidents.toDate")}
            type="date"
            value={filters.dateRange.end || ""}
            onChange={(e) =>
              onChange({ dateRange: { ...filters.dateRange, end: e.target.value || null } })
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={SORT_OPTIONS}
            value={filters.sortBy}
            onChange={(value) => onChange({ sortBy: value || "createdDate" })}
            label={t("incidents.sortBy")}
            disableClearable
          />
        </FilterCell>
        <FilterCell>
          <IconAutocomplete
            options={sortOrderOptions}
            value={filters.sortOrder}
            onChange={(value) => onChange({ sortOrder: value || "desc" })}
            label={t("incidents.sortOrder")}
            disableClearable
          />
        </FilterCell>
      </Box>
    </FilterPanel>
  );
}
